import React, { useState, useEffect, useCallback } from "react";
import { API } from "../../../config/api";
import { get } from "../../../lib/http";
import { formatDateTimeIST } from "../../../utils/datetime.js";

const PaymentErrors = () => {
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, today, week
  const [searchTerm, setSearchTerm] = useState("");

  const fetchErrors = useCallback(async () => {
    try {
      setLoading(true);
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      
      // Determine time range based on filter
      let timeRange;
      if (filter === "today") {
        timeRange = 24 * 60 * 60 * 1000; // 1 day
      } else if (filter === "week") {
        timeRange = 7 * 24 * 60 * 60 * 1000; // 7 days
      } else {
        timeRange = 30 * 24 * 60 * 60 * 1000; // 30 days for "all"
      }

      const url = `${API.paymentReconciliation.errors}?timeRange=${timeRange}&limit=100`;
      
      const res = await get(url, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
      });

      const errorData = res?.errors || [];
      setErrors(errorData);
    } catch (err) {
      console.error("Failed to fetch errors:", err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchErrors();
  }, [fetchErrors]);

  const filteredErrors = errors.filter((error) => {
    const matchesSearch =
      searchTerm === "" ||
      (error.paymentId && error.paymentId.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (error.customerEmail && error.customerEmail.toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesSearch;
  });

  const errorTypeCounts = errors.reduce((acc, error) => {
    acc[error.errorType] = (acc[error.errorType] || 0) + 1;
    return acc;
  }, {});

  const getErrorColor = (type) => {
    const colors = {
      PAYMENT_TIMEOUT: "bg-yellow-100 text-yellow-800",
      WEBHOOK_SIGNATURE_MISMATCH: "bg-red-100 text-red-800",
      TRANSACTION_FAILED: "bg-orange-100 text-orange-800",
      DUPLICATE_PAYMENT: "bg-purple-100 text-purple-800",
    };
    return colors[type] || "bg-gray-100 text-gray-800";
  };

  const getStatusColor = (status) => {
    return status === "resolved"
      ? "bg-green-100 text-green-800"
      : "bg-yellow-100 text-yellow-800";
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          🚨 Payment Error Monitor
        </h1>
        <p className="text-gray-600">Track and resolve payment errors</p>
      </div>

      {/* Error Type Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {Object.entries(errorTypeCounts).map(([type, count]) => (
          <div key={type} className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500 mb-1">{type}</h3>
            <p className="text-2xl font-bold text-gray-900">{count}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <input
            type="text"
            placeholder="Search by Payment ID or Email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
          </select>
          <button
            onClick={fetchErrors}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Errors Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Error Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center">
                    Loading...
                  </td>
                </tr>
              ) : filteredErrors.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-500">
                    No errors found
                  </td>
                </tr>
              ) : (
                filteredErrors.map((error) => (
                  <tr key={error.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDateTimeIST(error.timestamp)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${getErrorColor(
                          error.errorType
                        )}`}
                      >
                        {error.errorType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                      {error.paymentId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ₹{error.amount.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {error.customerEmail}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                          error.status
                        )}`}
                      >
                        {error.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button className="text-blue-600 hover:text-blue-900 mr-3">
                        View
                      </button>
                      {error.status === "pending" && (
                        <button className="text-green-600 hover:text-green-900">
                          Retry
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PaymentErrors;
